import clsx from "clsx";
import type { ReactNode } from "react";
import { Icon, type IconName } from "../Icon/Icon";
import styles from "./InlineAlert.module.css";

export interface InlineAlertProps {
  tone?: "info" | "success" | "warning" | "danger";
  /** What happened, in the user's terms. */
  title: ReactNode;
  /** Why, if known, and how to fix it. */
  children?: ReactNode;
  /** A Retry button or a link to where it can be fixed. */
  action?: ReactNode;
  className?: string;
}

const TONE_ICONS: Record<NonNullable<InlineAlertProps["tone"]>, IconName> = {
  info: "info",
  success: "checkCircle",
  warning: "alertTriangle",
  danger: "alertCircle",
};

/** A banner inside the page for errors that aren't tied to a field (with Retry), warnings and notices. */
export function InlineAlert({ tone = "info", title, children, action, className }: InlineAlertProps) {
  return (
    <div className={clsx(styles.alert, className)} data-tone={tone} role={tone === "danger" ? "alert" : "status"}>
      <span className={styles.icon}>
        <Icon name={TONE_ICONS[tone]} />
      </span>
      <div className={styles.text}>
        <p className={styles.title}>{title}</p>
        {children ? <div className={styles.body}>{children}</div> : null}
      </div>
      {action ? <div className={styles.action}>{action}</div> : null}
    </div>
  );
}
